import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import { X } from 'lucide-react';
import { formatLoginDate, getOutcomeColor } from '../utils/loginHistory';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '85%',
  maxWidth: 1000,
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  p: 4,
  overflow: 'auto',
};

const LoginHistoryModal = ({ open, onClose, user, accessToken }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLoginHistory = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/users/${user.id}/login-history?limit=50`,
        {
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch login history');
      }

      const data = await response.json();
      setEntries(data.loginHistory || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching login history:', err);
      setEntries([]);
      setError('Could not load login history for this user.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, user?.id]);

  useEffect(() => {
    if (open && user?.id) {
      fetchLoginHistory();
    }
  }, [open, user?.id, fetchLoginHistory]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            {user ? `Login History for ${user.name} (${user.email})` : 'Login History'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={fetchLoginHistory}>Retry</Button>}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? null : entries.length === 0 ? (
          <Typography>No login history found for this user.</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Connection</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Dashboard</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{formatLoginDate(entry.createdAt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={entry.outcome}
                        size="small"
                        sx={{
                          bgcolor: `${getOutcomeColor(entry.outcome)}20`,
                          color: getOutcomeColor(entry.outcome),
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell>{entry.connection || '—'}</TableCell>
                    <TableCell>{entry.roleAtLogin || '—'}</TableCell>
                    <TableCell>{entry.tenantKey || '—'}</TableCell>
                    <TableCell>{entry.ip || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.clientId || '—'}
                    </TableCell>
                    <TableCell>{entry.failureReason || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Modal>
  );
};

export default LoginHistoryModal;
